import React, { forwardRef } from "react"
import PropTypes from "prop-types"
import cn from "classnames"

import { SvgNormalizer } from "@asphalt-react/svg-normalizer"
import { svgUtil, propsUtil } from "@asphalt-react/helper"
import { Qualifier } from "@asphalt-react/qualifier"

import { NewTab } from "./new-tab.js"
import {
  svgSize,
  opensInNewTab,
  getRelAttributes,
  resolveSizeProp,
} from "./helper.js"
import { qualifierSVGSizes, iconSVGSizes } from "./values.js"

import styles from "./button.module.css"

const { isSVG } = svgUtil
const { concatClasses, resolvePropCollision } = propsUtil

export const Button = forwardRef(
  (
    {
      children,
      size = "m",
      primary = false,
      secondary = false,
      tertiary = false,
      nude = false,
      brand = false,
      danger = false,
      system = false,
      qualifier = null,
      qualifierEnd = false,
      stretch = false,
      icon = false,
      compact = false,
      link = false,
      as: Link = "a",
      asProps = { href: "." },
      stickStart = false,
      stickEnd = false,
      stick = false,
      underline = true,
      ...props
    },
    ref
  ) => {
    const { collision: variantCollision, value: variant } =
      resolvePropCollision(
        {
          primary,
          secondary,
          tertiary,
          nude,
        },
        "primary"
      )

    if (variantCollision) {
      console.warn(
        `Button: Multiple variants detected, falling back to "primary"`
      )
    }

    const { collision: intentCollision, value: intent } = resolvePropCollision(
      {
        brand,
        danger,
        system,
      },
      "brand"
    )

    if (intentCollision) {
      console.warn(`Button: Multiple intents detected, falling back to "brand"`)
    }

    /* eslint-disable-next-line react/prop-types */
    const { style, className, ...rest } = props

    const isCompactIcon = icon && compact
    const isCompactButton = !icon && compact
    const resolvedSize = resolveSizeProp(size)

    const compactIconClass = cn(
      styles.compactIcon,
      styles[`${concatClasses("compact", "icon", resolvedSize)}`]
    )

    const compactClass = cn(styles[`${concatClasses("compact", resolvedSize)}`])

    const btnClasses = cn(
      styles.Button,
      styles[variant],
      styles[`${concatClasses(variant, intent)}`],
      { [styles[`${concatClasses("button", resolvedSize)}`]]: !icon },
      { [styles[`${concatClasses("icon", resolvedSize)}`]]: icon },
      {
        [compactIconClass]: isCompactIcon,
        [compactClass]: isCompactButton,
      },
      { [styles.link]: link },
      { [styles.stretch]: stretch },
      { [styles.stickStart]: stickStart },
      { [styles.stickEnd]: stickEnd },
      { [styles[`${concatClasses("stick", variant)}`]]: stick },
      { [styles.noUnderline]: link && !underline }
    )

    /**
     * Add NewTab icon as default to hint that the link opens in a new tab (or window)
     */
    if (link && opensInNewTab(asProps?.target) && !qualifier) {
      qualifier = <NewTab />
    }

    const getQualifier = () => (
      <span
        data-testid="qualifier"
        className={cn(
          styles.qualifier,
          qualifierEnd ? styles.qualifierEnd : styles.qualifierStart
        )}
      >
        <div className={styles.qualifierWrapper}>
          <Qualifier iconSize={svgSize(qualifierSVGSizes, size)}>
            {qualifier}
          </Qualifier>
        </div>
      </span>
    )

    const getIcon = () => {
      if (icon && !isSVG(children)) {
        console.warn("Icon can only be SVG")
        children = ""
      }

      return (
        <div className={styles.iconWrapper}>
          <SvgNormalizer dimension={svgSize(iconSVGSizes, size)}>
            {children}
          </SvgNormalizer>
        </div>
      )
    }

    const getQualifierWithChildren = () => {
      return (
        <>
          {qualifier && !qualifierEnd && getQualifier()}
          {children}
          {qualifier && qualifierEnd && getQualifier()}
        </>
      )
    }

    if (link) {
      const { style, ...restAsProps } = asProps || {}

      /* eslint-disable-next-line react/prop-types */
      if (props.disabled) {
        console.warn("Button: LinkButton can not be disabled")
      }

      return (
        <Link
          {...getRelAttributes(asProps?.target)}
          {...restAsProps}
          className={btnClasses}
          ref={ref}
        >
          {icon ? getIcon() : getQualifierWithChildren()}
        </Link>
      )
    }

    if (icon) {
      return (
        <button type="button" {...rest} className={btnClasses} ref={ref}>
          {getIcon()}
        </button>
      )
    }

    return (
      <button type="button" {...rest} className={btnClasses} ref={ref}>
        {getQualifierWithChildren()}
      </button>
    )
  }
)

Button.displayName = "Button"

Button.propTypes = {
  /**
   * React node for button's caption
   */
  children: PropTypes.node.isRequired,
  /**
   * Renders a primary variant
   */
  primary: PropTypes.bool,
  /**
   * Renders a secondary variant
   */
  secondary: PropTypes.bool,
  /**
   * Renders a tertiary variant
   */
  tertiary: PropTypes.bool,
  /**
   * Renders a nude variant
   */
  nude: PropTypes.bool,
  /**
   * Renders brand intent
   */
  brand: PropTypes.bool,
  /**
   * Renders danger intent
   */
  danger: PropTypes.bool,
  /**
   * Renders system intent
   */
  system: PropTypes.bool,
  /**
   * Renders a link button
   */
  link: PropTypes.bool,
  /**
   * Link element to render. Accepts an HTML element or a React component.
   */
  as: PropTypes.elementType,
  /**
   * Accepts props & attributes for the link element.
   */
  asProps: PropTypes.object,
  /**
   * Renders an icon button
   */
  icon: PropTypes.bool,
  /**
   * Shrinks the size of the Button keeping the size of the contents intact. Useful for use-cases with space-constraints.
   */
  compact: PropTypes.bool,
  /**
   * Qualifier is an icon or a word that enhances the caption. Button prepends the qualifier by default. Accepts SVG for icons. Checkout `@asphalt-react/iconpack` for SVG wrapped React components.
   *
   * > ⚠️ Do not use `qualifier` to render an icon button, use `icon` prop instead
   */
  qualifier: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  /**
   * Appends qualifier to the caption
   */
  qualifierEnd: PropTypes.bool,
  /**
   * Controls size of button. Possible values are "xs", "s", "m", "l" for extra small, small, medium & large respectively
   */
  size: PropTypes.oneOf(["xs", "s", "m", "l"]),
  /**
   * Stretches Button to take it's container width
   */
  stretch: PropTypes.bool,
  /**
   * Adapts to let other buttons stick to its start
   */
  stickStart: PropTypes.bool,
  /**
   * Adapts to let other buttons stick to its end
   */
  stickEnd: PropTypes.bool,
  /**
   * Enhances the style of the supporting button.
   */
  stick: PropTypes.bool,
  /**
   * Adds underline in link Button
   */
  underline: PropTypes.bool,
}

Button.defaultProps = {
  size: "m",
  stretch: false,
  primary: false,
  secondary: false,
  tertiary: false,
  nude: false,
  brand: false,
  danger: false,
  system: false,
  qualifier: null,
  qualifierEnd: false,
  icon: false,
  compact: false,
  link: false,
  as: "a",
  asProps: { href: "." },
  stickEnd: false,
  stickStart: false,
  stick: false,
  underline: true,
}
